import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  INVENTORY_COUNT_MUTATION_KEY,
  useUpsertCount,
  type UpsertCountVariables,
} from "@/hooks/useInventories";
import {
  CURRENT_TAB_ID,
  INVALID_COUNT_REASON,
  listPendingInventoryIds,
  parseCountValue,
  readPendingCounts,
  refusePendingCount,
  retryRefusedCount,
  type PendingCount,
} from "@/lib/inventoryPendingCounts";

const SWEEP_INTERVAL_MS = 30_000;
// Saisie tapée à l'instant dans cet onglet : sa page l'envoie elle-même (debounce)
const RECENT_TYPING_MS = 2_000;
// Une requête bloquée (changement de réseau en cours de route) ne doit pas figer les balayages
const STATUS_TIMEOUT_MS = 15_000;

function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === "function") return AbortSignal.timeout(ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/**
 * Garantit que chaque saisie d'inventaire gardée sur l'appareil a une écriture en cours, quelle
 * que soit la page affichée : au démarrage (page rechargée, navigateur fermé ou appareil redémarré
 * avant l'envoi), au retour du réseau, quand l'onglet redevient visible et toutes les 30 s.
 *
 * Règles qui le rendent sûr par construction :
 * - il ne tourne que dans l'onglet visible (un seul onglet envoie à la fois) ;
 * - il relit la sauvegarde après chaque attente (une saisie a pu changer entre-temps) ;
 * - il n'efface jamais rien : un inventaire absent de la réponse (supprimé, ou session refusée
 *   par la RLS, qui renvoie alors 0 ligne sans erreur) est simplement laissé de côté ;
 * - sa requête a un délai maximal.
 * Filet de sécurité commun : une écriture dont la version n'est plus la dernière sauvegardée
 * n'écrit rien (useUpsertCount), un envoi en retard ne peut donc pas écraser une saisie récente.
 *
 * Ne renvoie pas : une saisie déjà en file (même version) ou tapée à l'instant, une saisie
 * refusée (elle attend une correction ou une annulation), une saisie d'un inventaire validé
 * (gardée et signalée sur la synthèse). Exception : une saisie refusée en 403 (inventaire validé
 * pendant l'envoi) est retentée une fois quand l'inventaire est rouvert.
 */
export function InventoryPendingSync() {
  const { user } = useOrganization();
  const queryClient = useQueryClient();
  const { mutate } = useUpsertCount();
  const runningRef = useRef(false);

  useEffect(() => {
    if (!user) return;

    const pendingRevs = () =>
      new Set(
        queryClient
          .getMutationCache()
          .findAll({ mutationKey: INVENTORY_COUNT_MUTATION_KEY, status: "pending" })
          .map((mutation) => (mutation.state.variables as UpsertCountVariables | undefined)?.rev),
      );
    // Saisie dont le balayage n'a pas à s'occuper maintenant. « Toute fraîche » ne vaut que pour
    // cet onglet : après un rechargement, aucune page n'a plus de debounce en cours pour elle.
    const isHandled = (entry: PendingCount, inFlight: Set<string | undefined>) =>
      inFlight.has(entry.rev) ||
      (entry.typedInTab === CURRENT_TAB_ID && Date.now() - entry.typedAt < RECENT_TYPING_MS);

    const sweep = async () => {
      if (runningRef.current || document.visibilityState !== "visible") return;
      runningRef.current = true;
      try {
        const inFlight = pendingRevs();
        const inventoryIds = new Set<string>();
        for (const inventoryId of listPendingInventoryIds()) {
          for (const [itemId, entry] of Object.entries(readPendingCounts(inventoryId))) {
            if (isHandled(entry, inFlight)) continue;
            // Valeur invalide restée sur l'appareil (page rechargée avant le debounce) : signalée
            if (!entry.refused && parseCountValue(entry.value) === undefined) {
              refusePendingCount(inventoryId, itemId, entry.rev, INVALID_COUNT_REASON);
              continue;
            }
            if (entry.refused && entry.refusedStatus !== 403) continue;
            inventoryIds.add(inventoryId);
          }
        }
        if (inventoryIds.size === 0) return;

        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) return;
        const { data, error } = await supabase
          .from("inventories")
          .select("id, status, updated_at")
          .in("id", [...inventoryIds])
          .abortSignal(timeoutSignal(STATUS_TIMEOUT_MS));
        // Réseau absent ou instable : nouvel essai au prochain passage
        if (error || !data) return;
        const inventoriesById = new Map(data.map((inventory) => [inventory.id, inventory]));

        // Relecture après l'attente : une saisie a pu être retapée, confirmée ou mise en file
        const inFlightNow = pendingRevs();
        for (const inventoryId of inventoryIds) {
          const inventory = inventoriesById.get(inventoryId);
          if (!inventory || inventory.status !== "draft") continue;
          for (const [itemId, entry] of Object.entries(readPendingCounts(inventoryId))) {
            if (isHandled(entry, inFlightNow)) continue;
            const quantity = parseCountValue(entry.value);
            if (quantity === undefined) continue;
            if (entry.refused) {
              if (entry.refusedStatus !== 403 || entry.retriedFor === inventory.updated_at) continue;
              retryRefusedCount(inventoryId, itemId, entry.rev, inventory.updated_at);
            }
            mutate({ inventory_id: inventoryId, item_id: itemId, quantity, rev: entry.rev });
          }
        }
      } finally {
        runningRef.current = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") sweep();
    };
    sweep();
    const timer = setInterval(sweep, SWEEP_INTERVAL_MS);
    window.addEventListener("online", sweep);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", sweep);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [user, queryClient, mutate]);

  return null;
}
